import { Action } from 'redux';
import {
	changeIdentifierInputValue,
	clearLoggedInMessage,
	closeSendmailEndDialogById,
	closeSendmailEndDialogByPw,
	IdentifierInputValue,
	ILoggedInMessage,
	INoticeMessage,
	selectPwrIdentifier,
	setIdreminderOpen,
	setLoggedInMessage,
	setLoginIDPwrChangePassword,
	setLoginLogo,
	setLoginManagerNotice,
	setPasswordChangeSuccess,
	setPwreminderOpen,
	setPwrIdentifierKey,
	setSendmailEndDialog,
	showLoginProgress,
} from 'src/actions/login';
import { initializeState } from 'src/utils/Initial';
import { PwrIdentificationItemType } from 'src/utils/Transfers';
import { isType } from 'typescript-fsa';
import i18n from '../resources/i18n';

/**
 * ログイン画面のState型
 */
export interface ILoginState {
	/** お知らせカルーセルのスライド間隔 */
	readonly carouselChangeInterval: number;
	/** お知らせデータ取得日時 */
	readonly fetchedMessagesAt?: Date;
	/** プログレス表示フラグ */
	readonly isShowProgress: boolean;
	/** お知らせデータリスト */
	readonly messages: INoticeMessage[];
	/** ログイン後メッセージ */
	readonly loggedInMessage: ILoggedInMessage;
	/** パスワード変更成功フラグ */
	readonly passwordChangeSuccess: boolean;
	/** パスワードリマインダ系プロパティ */
	readonly pwr: {
		/** 検証対象項目リスト */
		readonly identificationItems: PwrIdentificationItemType[];
		/** 選択中の対象項目 */
		readonly identifyInput: IdentifierInputValue;
		/** ログインID/EMAIL */
		readonly identifierKey: string;
		/** 「パスワードを忘れた場合はこちら」リンク表示制御 */
		readonly passwordLinkEnabled: boolean;
		/** 「IDを忘れた場合はこちら」リンク表示制御 */
		readonly loginIdLinkEnabled: boolean;
		/** パスワード変更のログインId */
		readonly loginID: string;
		/** メール送信完了のopenFlg */
		readonly sendmailEndOpen: boolean;
		/** ログインID通知およびパスワード再発行のopenFlg */
		readonly idreminderOpen: boolean;
		/** パスワード再発行のopenFlg */
		readonly pwreminderOpen: boolean;
		/** パスワード変更成功フラグ */
		readonly passwordChangeSuccess: boolean;
	};
	/** CMオプション表示フラグ */
	readonly showCMOption: boolean;
	/** 言語表示フラグ */
	readonly showLangOption: boolean;
}

/**
 * ログイン画面のState初期値
 */
const initialState: ILoginState = {
	carouselChangeInterval: 10000,
	isShowProgress: false,
	loggedInMessage: {
		displayContentsLicenseInfoMessage: false,
		displayDesktopMessageDialog: false,
		displayLastLoginIPAddress: false,
		displayLastLoginInfo: false,
		displaySecuritySamePwdWarningMessage: false,
	},
	messages: [
		{
			contact: '',
			message: [i18n.t('login_Reducers_0001')],
			startDate: new Date().toString(),
			titleList: [''],
		},
	],
	passwordChangeSuccess: false,
	pwr: {
		identificationItems: [],
		identifierKey: '',
		identifyInput: {},
		loginID: '',
		loginIdLinkEnabled: false,
		passwordChangeSuccess: false,
		passwordLinkEnabled: false,
		// tslint:disable-next-line: object-literal-sort-keys
		idreminderOpen: false,
		pwreminderOpen: false,
		sendmailEndOpen: false,
	},
	showCMOption: false,
	showLangOption: false,
};

const defaultValueForIdentifyInputTypeOf = (
	type: PwrIdentificationItemType | undefined
): IdentifierInputValue =>
	type === 'PWREMIND_SHOW_BIRTHDAY' || type === 'IDREMIND_SHOW_BIRTHDAY'
		? {
				entity: {
					day: '',
					month: '',
					year: '',
				},
				type,
		  }
		: type
		? {
				entity: '',
				type,
		  }
		: {};

/**
 * ログイン画面用reducer
 * @param state ログイン情報
 * @param action アクション
 */
const login = (state: ILoginState, action: Action): ILoginState => {
	if (!state) {
		// State初期化処理
		state = initialState;
		state = initializeState(state);
	}
	if (isType(action, showLoginProgress)) {
		return {
			...state,
			isShowProgress: action.payload.isShowProgress,
		};
	}

	if (isType(action, setLoginManagerNotice)) {
		const pwr = action.payload.pwr;
		const identifyInputType =
			pwr.identificationItems.length > 0
				? pwr.identificationItems[0]
				: undefined;

		return {
			...state,
			...action.payload,
			fetchedMessagesAt: new Date(),
			pwr: {
				...state.pwr,
				...pwr,
				identifyInput: defaultValueForIdentifyInputTypeOf(
					identifyInputType
				),
			},
		};
	}

	if (isType(action, setLoginIDPwrChangePassword)) {
		return {
			...state,
			pwr: {
				...state.pwr,
				loginID: action.payload.loginID,
			},
		};
	}

	if (isType(action, setSendmailEndDialog)) {
		const { open: sendmailEndOpen } = action.payload;
		return {
			...state,
			pwr: {
				...state.pwr,
				sendmailEndOpen,
			},
		};
	}

	if (isType(action, closeSendmailEndDialogById)) {
		return {
			...state,
			pwr: {
				...state.pwr,
				idreminderOpen: action.payload.idreminderOpen,
				sendmailEndOpen: action.payload.sendmailEndOpen,
			},
		};
	}

	if (isType(action, closeSendmailEndDialogByPw)) {
		return {
			...state,
			pwr: {
				...state.pwr,
				pwreminderOpen: action.payload.pwreminderOpen,
				sendmailEndOpen: action.payload.sendmailEndOpen,
			},
		};
	}

	if (isType(action, setIdreminderOpen)) {
		const idItems = state.pwr.identificationItems.filter(
			x => x.indexOf('IDREMIND_SHOW_') === 0
		);
		const identifyInputType = idItems.length > 0 ? idItems[0] : undefined;

		return {
			...state,
			pwr: {
				...state.pwr,
				identifierKey: '',
				identifyInput: defaultValueForIdentifyInputTypeOf(
					identifyInputType
				),
				idreminderOpen: action.payload.idreminderOpen,
			},
		};
	}

	if (isType(action, setPwreminderOpen)) {
		const idItems = state.pwr.identificationItems.filter(
			x => x.indexOf('PWREMIND_SHOW_') === 0
		);
		const identifyInputType = idItems.length > 0 ? idItems[0] : undefined;
		return {
			...state,
			pwr: {
				...state.pwr,
				identifierKey: '',
				identifyInput: defaultValueForIdentifyInputTypeOf(
					identifyInputType
				),
				pwreminderOpen: action.payload.pwreminderOpen,
			},
		};
	}

	if (isType(action, setPwrIdentifierKey)) {
		return {
			...state,
			pwr: {
				...state.pwr,
				identifierKey: action.payload.identifierKey,
			},
		};
	}

	if (isType(action, setLoggedInMessage)) {
		return {
			...state,
			loggedInMessage: {
				...action.payload,
			},
		};
	}

	if (isType(action, clearLoggedInMessage)) {
		return {
			...state,
			loggedInMessage: {
				...initialState.loggedInMessage,
			},
		};
	}

	if (isType(action, setPasswordChangeSuccess)) {
		return {
			...state,
			pwr: {
				...state.pwr,
				passwordChangeSuccess: action.payload.passwordChangeSuccess,
			},
		};
	}

	// パスワードリマインダ: 検証項目選択
	if (isType(action, selectPwrIdentifier)) {
		return {
			...state,
			pwr: {
				...state.pwr,
				identifyInput: defaultValueForIdentifyInputTypeOf(
					action.payload.selected
				),
			},
		};
	}

	// パスワードリマインダ: 検証項目入力
	if (isType(action, changeIdentifierInputValue)) {
		return {
			...state,
			pwr: {
				...state.pwr,
				identifyInput: action.payload,
			},
		};
	}

	if (isType(action, setLoginLogo)) {
		return {
			...state,
			showCMOption: action.payload.showCMOption,
			showLangOption: action.payload.showLangOption,
		};
	}

	return state;
};

export default login;



// WEBPACK FOOTER //
// ./src/reducers/login.ts